import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";

// A type alias (not an interface) so rows satisfy GroupedBarChart's Record<string, unknown>.
export type LgdBarRow = { name: string; lgd: number };

export interface LgdSummary {
  // false since the monthly LGD pipeline; asOf is its latest month-end, e.g. "Sep'26".
  isStatic: boolean;
  asOf: string;
  // Actual LGD: net loss ÷ gross loss over all defaulted contracts, as a %.
  portfolioLgd: number;
  recoveryRate: number;
  // Segments with a smoothed all-terms curve (enough defaults for the model to draw one).
  assetIndustriesModeled: number;
  borrowerIndustriesModeled: number;
  byAssetIndustry: LgdBarRow[];
  byBorrowerIndustry: LgdBarRow[];
}

// null -- the LGD pipeline has not produced a month yet. Render an empty
// state, not a fabricated stat.
export function useLgd() {
  return useQuery({
    queryKey: ["lgd"],
    queryFn: () => apiGet<LgdSummary | null>("/lgd"),
  });
}

export type LgdSmoothingPoint = {
  month: string;
  actualLgd: number | null;
  smoothedLgd: number | null;
};

// One column per contract term_range alongside `month`.
export type LgdTermRow = { month: string; [termRange: string]: string | number | null };

export interface LgdLowess {
  industries: string[];
  termRanges: string[];
  smoothing: LgdSmoothingPoint[];
  byTerm: LgdTermRow[];
}

/** Which industries the smoothing charts (and their Industry filter) cover. */
export type LgdGroup = "asset" | "borrower";

/** `terms` undefined = every contract term. Pass the selected term_range values otherwise.
 *  Terms filter only `byTerm`; `smoothing` is always the industry's all-terms line. `group` picks asset or borrower
 *  industries: the `industry` given must belong to it. */
export function useLgdLowess(
  industry: string | undefined,
  terms: string[] | undefined,
  group: LgdGroup = "asset",
  enabled = true,
) {
  return useQuery({
    queryKey: ["lgd", "lowess", group, industry ?? null, terms ? [...terms].sort().join("|") : null],
    queryFn: () =>
      apiGet<LgdLowess>("/lgd/lowess", {
        industry,
        terms: terms?.join(","),
        group,
      }),
    enabled,
    placeholderData: keepPreviousData,
  });
}
